import React, { useEffect, useState } from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { UserReportTable } from "./components/UserReportTable";
import { exportToPDF } from "../utils/exportToPdf";
import { exportToExcel } from "./utils/exportToExcel";
import { usePortal } from "../../../../context/PortalsContext";
import {
  previewReportTemplate,
  runReportTemplate,
} from "../../../../services/api/apiClient";
import {
  appliedFiltersType,
  ReportTemplateBodyParams,
  ReportTemplateTypeEnum,
  userReportByPortalsType,
  userReportType,
} from "../../../../services/api/types/reports/reports";
import { Spinner } from "../../../../components/Spinner/Spinner";
import { useAuth } from "../../../../context/AuthContext";
import { UserReportByPortalsTable } from "./components/UserReportByPortalsTable";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { formatDate, formatTime, generateFileName } from "../utils/helpers";
import { ExportMenu } from "../components/ExportMenu";
import Logo from "../../../../authentication/components/Logo";
import { ListOperator } from "../../../../services/api/types/users/users";
import { useCustomisation } from "../../../../context/CustomisationContext";
import { ExpandableFiltersSection } from "../components/ExpandableFiltersSection";

const MANUAL_SELECTED_USERS_IDS_KEY = "manualSelectedUsersIds";
const FILTERED_SELECTED_USERS_IDS_KEY = "filteredSelectedUsersIds";
const MANUAL_SELECTED_USERS_KEY = "manualSelectedUsers";
const FILTERED_SELECTED_USERS_KEY = "filteredSelectedUsers";
const SELECTED_USERS_SOURCE_KEY = "selectedUsersSource";

const getSelectedUserNames = (storageKey: string): string[] => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return [];
    const users = JSON.parse(stored) as Array<{
      firstName?: string;
      lastName?: string;
      email?: string;
    }>;
    if (!Array.isArray(users)) return [];
    return users.map((user) => {
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
      return fullName || user.email || "Unknown user";
    });
  } catch (error) {
    console.error("Error parsing selected users:", error);
    return [];
  }
};

interface UserReportProps {
  previewTemplateData?: ReportTemplateBodyParams;
  previewPortalId?: string;
  isPreviewMode?: boolean;
}

export const UserReport: React.FC<UserReportProps> = ({
  previewTemplateData,
  previewPortalId,
  isPreviewMode,
}) => {
  const [userData, setUserData] = useState<
    userReportType[] | userReportByPortalsType[]
  >([]);
  const [appliedFilters, setAppliedFilters] = useState<appliedFiltersType>({});
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [isGroupedByPortal, setIsGroupedByPortal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUserNames, setSelectedUserNames] = useState<string[]>([]);
  // Stable flag for rendering: the "viewReportMode" localStorage key is cleared
  // inside the effect, so we capture it in state to keep the UI consistent.
  const [isViewReportMode, setIsViewReportMode] = useState(false);

  const { portals } = usePortal();
  const { auth } = useAuth();
  const location = useLocation();
  const { portalUid: urlPortalUid } = useParams();
  const [searchParams] = useSearchParams();

  const { customisations } = useCustomisation();
  const {
    GPC: { navbarSettings },
  } = customisations.customisations;

  const gpcNavbarBgColor = navbarSettings?.navbarBgColor || undefined;

  // Check if this is "view report" mode from Users page
  const viewReportMode = localStorage.getItem("viewReportMode") === "true";

  // Check if preview mode: prop OR location.state (but not viewReportMode)
  const previewMode =
    ((isPreviewMode ||
      (previewTemplateData !== undefined && previewPortalId !== undefined) ||
      (location.state as { previewMode?: boolean })?.previewMode) &&
      !viewReportMode) ||
    false;

  const currentDate = new Date();
  const formattedDate = formatDate(currentDate);
  const formattedTime = formatTime(currentDate);

  // Get user name from auth context
  const userName = auth.user
    ? `${auth.user.firstName || ""} ${auth.user.lastName || ""}`.trim() ||
      "Unknown User"
    : "Unknown User";

  useEffect(() => {
    if (viewReportMode) {
      // View Report mode: read selected user IDs from localStorage
      const selectionSource = localStorage.getItem(
        SELECTED_USERS_SOURCE_KEY,
      ) as "manual" | "filtered" | null;

      // Determine which key to use
      const idsKey =
        selectionSource === "filtered"
          ? FILTERED_SELECTED_USERS_IDS_KEY
          : MANUAL_SELECTED_USERS_IDS_KEY;
      const usersKey =
        selectionSource === "filtered"
          ? FILTERED_SELECTED_USERS_KEY
          : MANUAL_SELECTED_USERS_KEY;

      let userNames = getSelectedUserNames(usersKey);
      if (userNames.length === 0) {
        const fallbackKey =
          usersKey === MANUAL_SELECTED_USERS_KEY
            ? FILTERED_SELECTED_USERS_KEY
            : MANUAL_SELECTED_USERS_KEY;
        userNames = getSelectedUserNames(fallbackKey);
      }
      setSelectedUserNames(userNames);
      setIsViewReportMode(true);

      // Try to get IDs from the determined key, fallback to both keys
      let selectedUserPortalUids: string[] = [];
      try {
        const storedIds = localStorage.getItem(idsKey);
        if (storedIds) {
          selectedUserPortalUids = JSON.parse(storedIds);
        } else {
          // Fallback: try both keys
          const manualIds = localStorage.getItem(MANUAL_SELECTED_USERS_IDS_KEY);
          const filteredIds = localStorage.getItem(
            FILTERED_SELECTED_USERS_IDS_KEY,
          );

          if (manualIds) {
            selectedUserPortalUids = JSON.parse(manualIds);
          } else if (filteredIds) {
            selectedUserPortalUids = JSON.parse(filteredIds);
          }
        }
      } catch (error) {
        console.error("Error parsing selected user IDs:", error);
      }

      const portalId =
        portals.selectedPortal?.portalUid ||
        sessionStorage.getItem("selectedPortal");

      if (!portalId || selectedUserPortalUids.length === 0) {
        console.error(
          "Missing portalId or selected user IDs in view report mode",
        );
        setLoading(false);
        return;
      }

      // Create fixed body structure with selected user UIDs
      const templateData: ReportTemplateBodyParams = {
        type: ReportTemplateTypeEnum.USER_REPORT,
        groupByPortal: false,
        filters: [
          {
            filterGroup: "userPortal",
            attribute: "PortalUid",
            operator: ListOperator.IN,
            value: portalId || "",
          },
          {
            filterGroup: "userPortal",
            attribute: "UserPortalUid",
            operator: ListOperator.IN,
            value: selectedUserPortalUids.join("|"),
          },
        ],
      };

      fetchPreviewData(portalId, templateData);

      // Clear the flag after use
      localStorage.removeItem("viewReportMode");
    } else if (previewMode) {
      // Preview mode: use props first, then location.state
      const templateData =
        previewTemplateData ||
        (location.state as { templateData?: ReportTemplateBodyParams })
          ?.templateData;
      const portalId =
        previewPortalId || (location.state as { portalId?: string })?.portalId;

      if (!templateData || !portalId) {
        console.error("Missing template data or portalId in preview mode");
        return;
      }

      fetchPreviewData(portalId, templateData);
    } else {
      // Normal mode: the portal and report template come from this tab's own
      // URL (so a report opened in a new tab always runs against the portal
      // and template it was opened with, not whatever another tab last set
      // in shared storage).
      const reportTemplateId = searchParams.get("reportTemplateId");
      const portalId = urlPortalUid || portals.selectedPortal?.portalUid;

      if (!portalId || !reportTemplateId) {
        console.error("Missing portalId or reportTemplateId");
        return;
      }
      fetchUserData(portalId, reportTemplateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlPortalUid,
    portals.selectedPortal,
    previewMode,
    previewTemplateData,
    previewPortalId,
    location.state,
    searchParams,
  ]);

  const fetchPreviewData = async (
    portalId: string,
    templateData: ReportTemplateBodyParams,
  ) => {
    if (!portalId || !templateData) return;
    setLoading(true);

    try {
      const response = await previewReportTemplate(portalId, templateData);

      if (!response.finResult) {
        console.error("Response does not have finResult property");
        setUserData([]);
        setIsGroupedByPortal(false);
        return;
      }

      // Extract appliedFilters from response
      if (response.finResult?.appliedFilters) {
        setAppliedFilters(response.finResult.appliedFilters);
      }

      // Detect structure: if first item has 'users' property, it's grouped by portal
      const firstItem = response.finResult.reportData[0];
      const isGrouped = "users" in firstItem && Array.isArray(firstItem.users);

      if (isGrouped) {
        setUserData(response.finResult.reportData as userReportByPortalsType[]);
        setIsGroupedByPortal(true);
      } else {
        setUserData(response.finResult.reportData as userReportType[]);
        setIsGroupedByPortal(false);
      }
    } catch (error: any) {
      console.error("Failed to fetch preview data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async (portalId: string, reportTemplateId: string) => {
    if (!portalId || !reportTemplateId) return;
    setLoading(true);

    try {
      const response = await runReportTemplate(
        portalId,
        reportTemplateId,
        auth.user?.UserPortalUid || "",
      );

      // Set timestamp to notify Reports page that a report was run
      const now = Date.now();
      const last = parseInt(
        localStorage.getItem("reportRunTimestamp") || "0",
        10,
      );
      const next = now > last ? now : last + 1;
      localStorage.setItem("reportRunTimestamp", next.toString());

      // Access finResult from the response
      if (!response.finResult) {
        console.error("Response does not have finResult property");
        setUserData([]);
        setIsGroupedByPortal(false);
        return;
      }

      // Extract appliedFilters from response
      if (response.finResult?.appliedFilters) {
        setAppliedFilters(response.finResult.appliedFilters);
      }

      // Detect structure: if first item has 'users' property, it's grouped by portal
      const firstItem = response.finResult.reportData[0];
      const isGrouped = "users" in firstItem && Array.isArray(firstItem.users);

      if (isGrouped) {
        setUserData(response.finResult.reportData as userReportByPortalsType[]);
        setIsGroupedByPortal(true);
      } else {
        setUserData(response.finResult.reportData as userReportType[]);
        setIsGroupedByPortal(false);
      }
    } catch (error: any) {
      console.error("Failed to fetch report data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setFiltersExpanded(true); // Ensure filters are expanded before export

    await exportToPDF(
      "user-report",
      generateFileName(ReportTemplateTypeEnum.USER_REPORT),
    );
  };

  const handleExportExcel = () => {
    exportToExcel({
      filters: appliedFilters,
      users: userData,
      reportName: "User Report",
      generatedBy: userName,
      time: formattedTime,
      isGroupedByPortal: isGroupedByPortal,
      fileName: generateFileName(ReportTemplateTypeEnum.USER_REPORT),
    });
  };

  return loading ? (
    <Spinner />
  ) : (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 0, md: 4 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        id="user-report"
        sx={{
          backgroundColor: "#fff",
          width: { xs: "100%", md: "842px" },
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          sx={{ backgroundColor: gpcNavbarBgColor, p: 1 }}
        >
          <Logo isLoginPageLogo={false} />
          {previewMode ? (
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "#667085",
              }}
            >
              Preview mode
            </Typography>
          ) : (
            <ExportMenu
              className="export-buttons"
              onExportPDF={handleExportPDF}
              onExportExcel={handleExportExcel}
            />
          )}
        </Stack>

        <Divider sx={{ borderColor: "#5354D8", mb: 2, mt: 3 }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="start"
          mb={2}
        >
          <Typography sx={{ color: "#1F2937", fontWeight: 600, fontSize: 16 }}>
            User Report
          </Typography>

          <Stack spacing={0.5} alignItems="start">
            <Box display="flex" alignItems="center" gap={0.75}>
              <CalendarTodayOutlinedIcon
                sx={{ fontSize: 12, color: "#667085" }}
              />
              <Typography sx={{ fontSize: 10, color: "#667085" }}>
                {formattedDate}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.75}>
              <AccessTimeOutlinedIcon sx={{ fontSize: 12, color: "#667085" }} />
              <Typography sx={{ fontSize: 10, color: "#667085" }}>
                {formattedTime}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.75}>
              <PersonOutlinedIcon sx={{ fontSize: 12, color: "#667085" }} />
              <Typography sx={{ fontSize: 10, color: "#667085" }}>
                {userName}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ borderColor: "#5354D8", mb: 2 }} />

        {/* Filters Section */}
        <ExpandableFiltersSection
          appliedFilters={appliedFilters}
          expanded={filtersExpanded}
          onExpandedChange={setFiltersExpanded}
        />

        {/* Table Section */}
        <Typography color="#1F2937" fontSize="14px" fontWeight={500} mb={3}>
          User details
        </Typography>
        {isGroupedByPortal ? (
          <UserReportByPortalsTable
            data={userData as userReportByPortalsType[]}
          />
        ) : (
          (() => {
            const flatUsers = userData as userReportType[];
            const portalMap = flatUsers.reduce<
              Record<string, userReportType[]>
            >((acc, user) => {
              const key = user.name || "";
              if (!acc[key]) acc[key] = [];
              acc[key].push(user);
              return acc;
            }, {});
            const groups = Object.entries(portalMap);
            if (groups.length <= 1) {
              return (
                <UserReportTable
                  users={flatUsers}
                  portalName={groups[0]?.[0] || undefined}
                />
              );
            }
            return (
              <>
                {groups.map(([name, users]) => (
                  <Box key={name} sx={{ mb: 4 }}>
                    <UserReportTable
                      users={users}
                      portalName={name || undefined}
                    />
                  </Box>
                ))}
              </>
            );
          })()
        )}
      </Box>
    </Box>
  );
};
